/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import MsDyn365 from '@msdyn365-commerce/core';
import { IInvoiceSummaryLines } from '@msdyn365-commerce-modules/invoice-payment-summary';
import { ArrayExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { INodeProps, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { ICartViewProps, IOrderSummaryErrors } from '@msdyn365-commerce-modules/cart/src/modules/cart/./cart';
import { ICartlinesViewProps } from '@msdyn365-commerce-modules/cart/src/modules/cart/./components/cart-line-items';
import { ICartLineItemViewProps } from '@msdyn365-commerce-modules/cart/src/modules/cart/./components/cart-line-items-promotion';
import { ICartConfig, ICartProps } from '../definition-extensions/cart.ext.props.autogenerated';
import * as onlyMethods from '../../../custom-proxy/DataActionExtension.g';
import { RichTextComponent } from '@msdyn365-commerce/core-internal';
import { Coupon } from '@msdyn365-commerce/retail-proxy';
import { getCartState } from '@msdyn365-commerce/global-state';
import { _OrderSummaryLine } from '../../../utils/orderSummaryLine';
import { CouponCard, ICouponCardProps } from '../../../shared/components/coupon-card';
import { CustomerServiceLinks, ICustomerServiceLinksProps } from '../../../shared/components/customer-service-links';
import { ICustomerServiceLinkData } from '../../../modules/only-c-checkout';

const _renderCartlines = (
    cartLines: ICartlinesViewProps[] | undefined,
    cartEmptyText: string,
    CartlinesWrapper: INodeProps,
    storeSelector: React.ReactNode | undefined,
    backToShoppingButton: React.ReactNode,
    waitingComponent: React.ReactNode,
    cartLoadingStatus: string,
    cartDataResult: boolean,
    config: ICartConfig
): JSX.Element[] | JSX.Element => {
    if (cartLoadingStatus) {
        return <>{cartLoadingStatus}</>;
    }
    if (cartLines) {
        return cartLines.map(cartLine => {
            return (
                <div className='msc-cart-lines-item' key={`${cartLine.cartlineId ?? ''}-${MsDyn365.isBrowser.toString()}`}>
                    <div className='custom-cart'>
                        {cartLine.cartline}
                        {storeSelector && cartLine.pickUpInStore ? (
                            <Node {...cartLine.pickUpInStore.ContainerProps}>{cartLine.pickUpInStore.defaultComponent}</Node>
                        ) : null}
                        {cartLine.addToOrderTemplate ? (
                            <Node className='msc-cart-line__extra-actions'>
                                {cartLine.remove}
                                {cartLine.addToWishlist}
                                {cartLine.addToOrderTemplate}
                            </Node>
                        ) : (
                            <>
                                {cartLine.remove}
                                {cartLine.addToWishlist}
                            </>
                        )}
                    </div>
                </div>
            );
        });
    }
    return cartDataResult ? (
        <div className='msc-cart__empty-cart'>
            <p className='msc-cart-line'>{cartEmptyText}</p>
            {/* {backToShoppingButton} */}
            <a className='terms-condition-links' href={config.continueShoppingLink?.linkUrl.destinationUrl}>
                {<RichTextComponent text={config.continueShoppingLink?.linkText} />}
            </a>
        </div>
    ) : (
        <>{waitingComponent}</>
    );
};

/**
 * Method will render error block.
 * @param errorData -The order summary errors interface.
 * @returns Jsx element.
 */
const _renderErrorBlock = (errorData: IOrderSummaryErrors | undefined): JSX.Element | null => {
    if (!errorData || !ArrayExtensions.hasElements(errorData.errors)) {
        return null;
    }
    return (
        <Node {...errorData.Wrapper}>
            {errorData.header}
            {errorData.errors}
        </Node>
    );
};

/**
 * Method will render invoice summary lines.
 * @param invoiceSummaryLines -The invoicesummary lines interface.
 * @param OrderSummaryItems -The order summary items props.
 * @param props -The cartview props.
 * @returns Jsx element.
 */
const _renderInvoiceSummarylines = (
    invoiceSummaryLines: IInvoiceSummaryLines | undefined,
    OrderSummaryItems: INodeProps,
    props: ICartViewProps & ICartProps<{}>
): JSX.Element | null => {
    if (!invoiceSummaryLines) {
        props.context.telemetry.error('InvoiceSummary content is empty, module wont render');
        return null;
    }
    return (
        <Node {...OrderSummaryItems}>
            {invoiceSummaryLines.invoices}
            {invoiceSummaryLines.giftCard}
            {invoiceSummaryLines.loyalty}
            {invoiceSummaryLines.orderTotal}
        </Node>
    );
};

/**
 * Method will render order summary lines.
 * @param orderSummaryLines -The ordersummary lines interface.
 * @param OrderSummaryItems -The order summary items props.
 * @param props -The cartview props.
 * @returns Jsx element.
 */
const _renderOrderSummarylines = (props: ICartViewProps & ICartProps<{}>): JSX.Element | null => {
    const [cuopons, setCuopons] = React.useState<Coupon[]>([]);
    const [errorText, setErrorText] = React.useState<string>('Coupon Cannot Be Applied');
    const [error, setError] = React.useState<boolean>(false);
    const [couponsDisable, setcouponsDisable] = React.useState<boolean>(false);

    const {
        resources: { useTextMessage, availDiscountMessage, validTillMessage },
        orderSummaryLineitems,
        OrderSummaryItems,
        context: {
            request: {
                user: { isAuthenticated }
            }
        }
    } = props;

    React.useEffect(() => {
        async function fetchCoupons() {
            const icontext = { callerContext: props.context.actionContext };
            const cartState = props.cart;
            const cartId = cartState?.cart.Id;
            if (icontext && cartId) {
                const coupons = await onlyMethods.bSGetCustomerCouponsAsync(icontext, cartId);
                if (coupons && coupons.length > 0) {
                    setCuopons(coupons);
                }
            }
        }

        if (isAuthenticated) {
            fetchCoupons()
                .then()
                .catch(error => {
                    props.telemetry.error(error);
                });
        }
    }, []);

    const applyAllCoupons = async (item: Coupon) => {
        setcouponsDisable(true);
        const icontext = { callerContext: props.context.actionContext };

        const cartState = await getCartState(props.context.actionContext);
        const cartId = cartState.cart.Id;

        const isApplied = {
            Key: 'BS_ISAPPLIED',
            Value: {
                BooleanValue: true
            }
        };

        item.ExtensionProperties!.push(isApplied);
        onlyMethods
            .bSAddRemoveCustomerCouponAsync(icontext, cartId, [item])
            .then(res => {
                if (res) {
                    setError(false);
                    props.data.cart.result?.refreshCart({});
                    setcouponsDisable(false);
                }
            })
            .catch(ex => {
                setErrorText(ex.message);
                setError(true);
                setcouponsDisable(false);
            });
    };

    if (!orderSummaryLineitems) {
        props.context.telemetry.error('OrderSummary content is empty, module wont render');
        return null;
    }

    const handleChange = (item: Coupon) => {
        applyAllCoupons(item);
    };

    return (
        <Node {...OrderSummaryItems}>
            {props.promoCode}

            {/* VSI Customization for promo code display */}
            <div>
                {cuopons &&
                    cuopons.map((item: Coupon) => {
                        const alreadyApplied = props.cart?.cart.Coupons?.find((items: Coupon) => items.Code === item.Code);
                        if (!alreadyApplied) {
                            const couponCardProps: ICouponCardProps = {
                                ...props,
                                coupon: item,
                                availDiscountMessage,
                                validTillMessage,
                                usePrefix: useTextMessage,
                                compactView: true
                            };

                            return (
                                <>
                                    <ul className='list-group'>
                                        <li className='list-group-item reward-card-wrapper'>
                                            <CouponCard {...couponCardProps} />
                                            <input
                                                maxLength={50}
                                                onChange={() => handleChange(item)}
                                                className='form-check-input me-1'
                                                type='checkbox'
                                                value={item.Code}
                                                aria-label='...'
                                                disabled={couponsDisable}
                                            />
                                        </li>
                                    </ul>
                                </>
                            );
                        }
                        return;
                    })}
                {error && <p>{errorText}</p>}
            </div>
            {/* VSI Customization - To display subamout without discount */}
            <_OrderSummaryLine
                price={props.cart?.cart?.NetPrice}
                label={props.resources.itemsWithCountLabel}
                context={props.context}
                id={props.id}
                typeName={props.typeName}
                toBeCalculatedText={props.resources.yetToBeCalculatedText}
                freeText={props.resources.priceFree}
                cssLabel='sub-total'
                telemetry={props.telemetry}
            />
            {/* {orderSummaryLines.subtotal} */}
            {orderSummaryLineitems.shipping}
            {orderSummaryLineitems.otherCharge}
            {/* {orderSummaryLineitems.tax} */}
            {orderSummaryLineitems.totalDiscounts ? orderSummaryLineitems.totalDiscounts : null}
            {orderSummaryLineitems.orderTotal}
        </Node>
    );
};

/**
 * Method will render cart promotions.
 * @param promotions -The cartline item view props.
 * @returns Jsx element.
 */
const _renderPromotions = (promotions: ICartLineItemViewProps): JSX.Element | undefined => {
    return (
        <>
            <Node {...promotions.promotionMessageWrapper}>
                {promotions.promotionMessage}
                {promotions.promotionSeeAllLink}
                {promotions.promotionDialog}
            </Node>
        </>
    );
};

/**
 * Method will render cartlines group title.
 * @param cartLine -The cartline view props.
 * @param titleSeparate -The title separate.
 * @returns Jsx element.
 */
const renderCartLinesGroupTitle = (cartLine: ICartlinesViewProps, titleSeparate: string) => {
    const isBopisSelected: boolean | undefined = cartLine.pickUpInStore?.isBopisSelected;
    return (
        <>
            <Node className='msc-cart-lines-group-wraper__bopis-heading-title'>
                {isBopisSelected ? (
                    <p className='msc-cart-lines-group-wraper__bopis-heading-title-st'>
                        {cartLine.pickUpInStore?.deliveryOption}
                        {titleSeparate}
                        {cartLine.pickUpInStore?.orgUnitName}
                    </p>
                ) : null}
            </Node>
        </>
    );
};

/**
 * Method will render cartlines groups header title.
 * @param pickUpText -The pickup text for group.
 * @param shippingText -The shipping text for group.
 * @param emailShippingText -The email shipping text for group.
 * @param titleSeparate -The title separator.
 * @param cartLine -The cartline view props.
 * @param emailDeliveryModeCode -The email delivery code from channel.
 * @returns Jsx element.
 */
const renderCartLinesGroupHeader = (
    pickUpText: string,
    shippingText: string,
    emailShippingText: string,
    titleSeparate: string,
    cartLine: ICartlinesViewProps,
    emailDeliveryModeCode?: string
) => {
    const isBopisSelected: boolean | undefined = cartLine.pickUpInStore?.isBopisSelected;
    let groupTitle: string;

    groupTitle = isBopisSelected ? pickUpText : shippingText;
    if (!StringExtensions.isNullOrEmpty(emailDeliveryModeCode)) {
        groupTitle = cartLine.data && cartLine.data.cartline.DeliveryMode === emailDeliveryModeCode ? emailShippingText : groupTitle;
    }

    return (
        <>
            <div className='msc-cart-lines-group-wraper__bopis-heading'>
                <p className={`msc-cart-lines-group-wraper__bopis-heading-${groupTitle.toLowerCase()}-icon`} />
                <p className={`msc-cart-lines-group-wraper__bopis-heading-${groupTitle.toLowerCase()}`}>{groupTitle}</p>
                {renderCartLinesGroupTitle(cartLine, titleSeparate)}
            </div>
        </>
    );
};

/**
 * Method will render cartlines in groups.
 * @param props -The cartline.
 * @returns Jsx element.
 */
const renderCartLinesGroup = (props: ICartViewProps): JSX.Element | undefined => {
    if (props.cartLinesGroup && ArrayExtensions.hasElements(props.cartLinesGroup)) {
        return (
            <div className='msc-cart-lines-group'>
                {props.cartLinesGroup.map(cartlines => {
                    return (
                        <div key={cartlines[0].cartlineId} className='msc-cart-lines-group-wraper'>
                            {renderCartLinesGroupHeader(
                                props.resources.pickUpText,
                                props.resources.shippingText,
                                props.resources.emailshippingText,
                                props.resources.titleSeparate,
                                cartlines[0],
                                props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode
                            )}
                            {_renderCartlines(
                                cartlines,
                                props.resources.emptyCartText,
                                props.CartlinesWrapper,
                                props.storeSelector,
                                props.backToShoppingButton,
                                props.waitingComponent,
                                props.cartLoadingStatus,
                                props.cartDataResult,
                                props.config
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }
    return props.cartDataResult ? (
        <div className='msc-cartline-wraper'>
            <div className='msc-cart__empty-cart'>
                <p className='msc-cart-line'>{props.resources.emptyCartText}</p>
                {props.backToShoppingButton}
            </div>
        </div>
    ) : (
        <>{props.waitingComponent}</>
    );
};

const CartView: React.FC<ICartViewProps & ICartProps<{}>> = (props: ICartViewProps & ICartProps<{}>) => {
    const {
        resources: { bagHeading, itemHeading },
        className,
        config: { backToShoppingLink, customerServiceLinks }
    } = props;
    const newBackToShoppingButton = React.cloneElement(props.backToShoppingButton as React.ReactElement, {
        href: backToShoppingLink?.linkUrl.destinationUrl
    });
    const links: ICustomerServiceLinksProps = { links: customerServiceLinks as ICustomerServiceLinkData[] | undefined };
    return (
        <div className={className} id={props.id} {...props.renderModuleAttributes(props)}>
            <div className='row custom-bag-count-box'>
                <div className='col-6'>
                    <div className='bag-head'>
                        <p>{bagHeading}</p>
                    </div>
                </div>
                <div className='col-6'>
                    <div className='bag-item'>
                        <p>
                            {props?.data?.cart?.result?.cart?.TotalItems} {itemHeading}
                        </p>
                    </div>
                </div>
            </div>
            {props.title}
            {!props.cart?.hasInvoiceLine && props.promotionOptions && _renderPromotions(props.promotionOptions)}
            <div className='msc-cart__content-wrapper'>
                {props.multiplePickUpEnabled ? (
                    renderCartLinesGroup(props)
                ) : (
                    <Node {...props.CartlinesWrapper}>
                        <div>
                            {_renderCartlines(
                                props.cartlines,
                                props.resources.emptyCartText,
                                props.CartlinesWrapper,
                                props.storeSelector,
                                props.backToShoppingButton,
                                props.waitingComponent,
                                props.cartLoadingStatus,
                                props.cartDataResult,
                                props.config
                            )}
                            {props.cartlines !== null && (
                                <div className='cart-feature desktop'>
                                    <CustomerServiceLinks {...links} />
                                </div>
                            )}
                        </div>
                    </Node>
                )}
                {props.orderSummaryHeading && (
                    <>
                        <Node {...props.OrderSummaryWrapper}>
                            {props.orderSummaryHeading}
                            {props.cart?.hasInvoiceLine
                                ? _renderInvoiceSummarylines(props.invoiceSummaryLineitems, props.OrderSummaryItems, props)
                                : _renderOrderSummarylines(props)}
                            {/* _renderOrderSummarylines(props.orderSummaryLineitems, props.OrderSummaryItems, props) */}
                            {_renderErrorBlock(props.OrderSummaryErrors)}
                            {!props.context.request.user.isAuthenticated ? props.checkoutAsSignInUserButton : (
                                <>
                                    <a href="./checkout" title={props.resources.checkoutButtonLoggedInTitle} data-exp-event-id="Cart Page.Cart.Checkout.click" className="msc-cart__btn-checkout msc-btn disabled">{props.resources.checkoutButtonLoggedInTitle}</a>
                                </>
                            )
                            }
                            {props.checkoutAsGuestButton}
                            {props.expressCheckoutButton && ArrayExtensions.hasElements(props.expressCheckoutButton) ? (
                                <Node {...props.ExpressCheckoutSectionWrapper}>{props.expressCheckoutButton}</Node>
                            ) : null}
                            {newBackToShoppingButton}
                            {props.createTemplateFromCartButton}
                        </Node>
                        <div className='cart-feature device'>
                            <CustomerServiceLinks {...links} />
                        </div>
                    </>
                )}
                {props.storeSelector}
            </div>
        </div>
    );
};

export default CartView;
